import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Search } from "lucide-react";
import { adminMe, adminSummary, adminList, adminRecord, adminRoster, getToken, clearToken, errText } from "@/lib/api";
import { TABS, STATUSES, inputCls, ghostBtnCls } from "@/components/admin/constants";
import { Login } from "@/components/admin/Login";
import { ListTable } from "@/components/admin/ListTable";
import { RosterTable } from "@/components/admin/RosterTable";
import { Detail } from "@/components/admin/Detail";
import { PartnersPanel } from "@/components/admin/PartnersPanel";
import { BroadcastPanel } from "@/components/admin/BroadcastPanel";
import { OrgSettingsPanel, ContactInbox } from "@/components/admin/OrgSettingsPanel";

const SummaryChips = ({ summary }) => {
  if (!summary) return null;
  const chips = [
    { k: summary.new_this_week, v: "New This Week", cls: "border-gold/60 text-goldlight" },
    { k: summary.needs_review, v: "Needs Review", cls: "border-silver/40 text-silver" },
    { k: summary.meetings_scheduled, v: "Meetings Scheduled", cls: "border-silver/25 text-silver/80" },
    { k: summary.active_partners, v: "Active Partners", cls: "border-[#3ad07a]/50 text-[#3ad07a]" },
  ];
  return (
    <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="summary-chips">
      {chips.map((c) => (
        <div key={c.v} className={"border bg-charcoal p-4 " + c.cls}>
          <span className="font-display text-4xl">{c.k}</span>
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-silver/60">{c.v}</p>
        </div>
      ))}
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("applications");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [roster, setRoster] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const isRoster = tab === "roster";
  const isPartners = tab === "partners";
  const isBroadcast = tab === "broadcast";
  const isSettings = tab === "settings";
  const isSpecial = isPartners || isBroadcast || isSettings;
  const detailKind = isRoster ? "applications" : tab;

  useEffect(() => {
    if (!getToken()) return setAuthed(false);
    adminMe().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "partners" || tab === "broadcast" || tab === "settings") {
        const s = await adminSummary();
        setSummary(s);
      } else if (tab === "roster") {
        const [s, r] = await Promise.all([adminSummary(), adminRoster()]);
        setSummary(s);
        setRoster(r);
      } else {
        const [s, list] = await Promise.all([
          adminSummary(),
          adminList(tab, { status: statusFilter || undefined, search: search || undefined }),
        ]);
        setSummary(s);
        setRows(list);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        setAuthed(false);
      } else {
        toast.error(errText(err));
      }
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter, search]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  if (authed === null) return <div className="bg-onyx py-40 text-center font-head font-bold uppercase text-silver/50">Opening the inbox...</div>;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const onUpdated = (updated) => {
    setRows((p) => p.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
    adminRecord(detailKind, updated.id).then(setSelected).catch(() => setSelected(updated));
    adminSummary().then(setSummary).catch(() => {});
    if (isRoster) adminRoster().then(setRoster).catch(() => {});
  };

  const openRecord = (row) => {
    setSelected(row);
    adminRecord(detailKind, row.id).then(setSelected).catch(() => {});
  };

  const openRosterRecord = (row) => {
    adminRecord("applications", row.application_id)
      .then(setSelected)
      .catch((e) => toast.error(errText(e)));
  };

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  const q = search.trim().toLowerCase();
  const rosterShown = q
    ? roster.filter((r) => [r.name, r.email, r.member_number, r.lane].some((v) => v && v.toLowerCase().includes(q)))
    : roster;

  return (
    <section className="grain-dark min-h-screen bg-onyx py-10 text-cream sm:py-14" data-testid="admin-inbox">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-marker text-lg text-goldlight">the inbox ✦</p>
            <h1 className="font-display text-5xl uppercase tracking-tight text-metallic">Submissions</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className={ghostBtnCls + " inline-flex items-center gap-2 px-4 py-2 text-xs"}>
            <LogOut size={14} /> Log out
          </button>
        </div>

        <SummaryChips summary={summary} />

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => { setTab(t.id); setStatusFilter(""); setSelected(null); }} data-testid={"admin-tab-" + t.id}
              className={"border px-4 py-2 font-head text-sm font-extrabold uppercase tracking-wide transition-colors " + (tab === t.id ? "border-gold bg-gradient-to-b from-goldlight to-gold text-onyx" : "border-silver/30 bg-charcoal text-silver hover:border-gold/60 hover:text-goldlight")}>
              {t.label}{summary ? " (" + summary.counts[t.id.replace(/-/g, "_")] + ")" : ""}
            </button>
          ))}
          <button onClick={() => { setTab("roster"); setStatusFilter(""); setSelected(null); }} data-testid="admin-tab-roster"
            className={"border px-4 py-2 font-head text-sm font-extrabold uppercase tracking-wide transition-colors " + (isRoster ? "border-gold bg-gradient-to-b from-goldlight to-gold text-onyx" : "border-silver/30 bg-charcoal text-goldlight hover:border-gold")}>
            Founding Cohort{roster.length && isRoster ? " (" + roster.length + ")" : ""}
          </button>
          <button onClick={() => { setTab("partners"); setStatusFilter(""); setSelected(null); }} data-testid="admin-tab-partners"
            className={"border px-4 py-2 font-head text-sm font-extrabold uppercase tracking-wide transition-colors " + (isPartners ? "border-gold bg-gradient-to-b from-goldlight to-gold text-onyx" : "border-silver/30 bg-charcoal text-goldlight hover:border-gold")}>
            Mission Wall
          </button>
          <button onClick={() => { setTab("broadcast"); setStatusFilter(""); setSelected(null); }} data-testid="admin-tab-broadcast"
            className={"border px-4 py-2 font-head text-sm font-extrabold uppercase tracking-wide transition-colors " + (isBroadcast ? "border-gold bg-gradient-to-b from-goldlight to-gold text-onyx" : "border-silver/30 bg-charcoal text-goldlight hover:border-gold")}>
            Broadcast
          </button>
          <button onClick={() => { setTab("settings"); setStatusFilter(""); setSelected(null); }} data-testid="admin-tab-settings"
            className={"border px-4 py-2 font-head text-sm font-extrabold uppercase tracking-wide transition-colors " + (isSettings ? "border-gold bg-gradient-to-b from-goldlight to-gold text-onyx" : "border-silver/30 bg-charcoal text-goldlight hover:border-gold")}>
            Settings
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          {!isSpecial && (
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-silver/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={isRoster ? "Search name, member #, lane..." : "Search name, email, school..."} data-testid="admin-search-input" className={inputCls + " pl-9 sm:w-72"} />
          </div>
          )}
          {!isRoster && !isSpecial && (
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="admin-status-filter" className={inputCls}>
              <option value="">All statuses</option>
              {STATUSES[tab].map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
              ))}
            </select>
          )}
        </div>

        {isSettings ? (
          <>
            <OrgSettingsPanel />
            <ContactInbox />
          </>
        ) : isBroadcast ? (
          <BroadcastPanel />
        ) : isPartners ? (
          <PartnersPanel />
        ) : isRoster ? (
          <RosterTable rows={rosterShown} loading={loading} onSelect={openRosterRecord} />
        ) : (
          <ListTable tab={tab} rows={rows} loading={loading} onSelect={openRecord} />
        )}
      </div>

      {selected && <Detail kind={detailKind} record={selected} onClose={() => setSelected(null)} onUpdated={onUpdated} />}
    </section>
  );
}
